import {
  ApEdition,
  ApFlagId,
  AuthenticationResponse,
  ErrorCode,
  isNil,
  SignInRequest,
} from '@activepieces/shared';
import { typeboxResolver } from '@hookform/resolvers/typebox';
import { Static, Type } from '@sinclair/typebox';
import { useMutation } from '@tanstack/react-query';
import { t } from 'i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { flagsHooks } from '@/hooks/flags-hooks';
import { HttpError, api } from '@/lib/api';
import { authenticationApi } from '@/lib/authentication-api';
import { authenticationSession } from '@/lib/authentication-session';
import { formatUtils } from '@/lib/utils';
import { GoogleIcon } from '@/assets/img/custom/auth/google-icon';
import { KizeoFormsIcon } from '@/assets/img/custom/auth/kizeo-forms-icon';

const SignInSchema = Type.Object({
  email: Type.String({
    pattern: formatUtils.emailRegex.source,
    errorMessage: t('Email is invalid'),
  }),
  password: Type.String({
    minLength: 1,
    errorMessage: t('Password is required'),
  }),
});

type SignInSchema = Static<typeof SignInSchema>;

const SignInForm: React.FC = () => {
  const form = useForm<SignInSchema>({
    resolver: typeboxResolver(SignInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  const { data: edition } = flagsHooks.useFlag(ApFlagId.EDITION);

  const { data: userCreated } = flagsHooks.useFlag(ApFlagId.USER_CREATED);
  const navigate = useNavigate();
  const googleMutation = useMutation<
    AuthenticationResponse,
    HttpError,
    { code: string }
  >({
    mutationFn: ({ code }) => authenticationApi.signInWithGoogle({ code }),
    onSuccess: (data) => {
      authenticationSession.saveResponse(data);
      navigate('/flows');
    },
    onError: (error) => {
      console.error('Google sign-in error', error);
    },
  });
  const kizeoMutation = useMutation<
    AuthenticationResponse,
    HttpError,
    { code: string }
  >({
    mutationFn: ({ code }) => authenticationApi.signInWithKizeo({ code }),
    onSuccess: (data) => {
      authenticationSession.saveResponse(data);
      navigate('/flows');
    },
    onError: (error) => {
      console.error('Kizeo sign-in error', error);
    },
  });
  const { mutate, isPending } = useMutation<
    AuthenticationResponse,
    HttpError,
    SignInRequest
  >({
    mutationFn: authenticationApi.signIn,
    onSuccess: (data) => {
      authenticationSession.saveResponse(data);
      navigate('/flows');
    },
    onError: (error) => {
      if (api.isError(error)) {
        const errorCode: ErrorCode | undefined = (
          error.response?.data as { code: ErrorCode }
        )?.code;
        if (isNil(errorCode)) {
          form.setError('root.serverError', {
            message: t('Something went wrong, please try again later'),
          });
          return;
        }
        switch (errorCode) {
          case ErrorCode.INVALID_CREDENTIALS: {
            form.setError('root.serverError', {
              message: t('Invalid email or password'),
            });
            break;
          }
          case ErrorCode.USER_IS_INACTIVE: {
            form.setError('root.serverError', {
              message: t('User has been deactivated'),
            });
            break;
          }
          case ErrorCode.EMAIL_IS_NOT_VERIFIED: {
            form.setError('root.serverError', {
              message: t(`Email hasn't been verified, check your inbox`),
            });
            break;
          }
          case ErrorCode.DOMAIN_NOT_ALLOWED: {
            form.setError('root.serverError', {
              message: t(`Email domain is disallowed`),
            });
            break;
          }
          case ErrorCode.EMAIL_AUTH_DISABLED: {
            form.setError('root.serverError', {
              message: t(`Email authentication has been disabled`),
            });
            break;
          }
          default: {
            form.setError('root.serverError', {
              message: t('Something went wrong, please try again later'),
            });
          }
        }
      }
    },
  });

  const onSubmit: SubmitHandler<SignInRequest> = (data) => {
    form.setError('root.serverError', {
      message: undefined,
    });
    mutate(data);
  };

  const signInWithGoogle = () => {
    const redirectUri = import.meta.env.VITE_REDIRECT_URI;
    const responseType = 'code';
    const scope = encodeURIComponent('email profile');
    const prompt = 'select_account';
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const state = generateRandomHex(48);
    const googleOauth2Url = 'https://accounts.google.com/o/oauth2/v2/';
    const authorizationUrl = `${googleOauth2Url}auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&state=${state}&prompt=${prompt}`;

    const width = 500;
    const height = 600;
    const popup = openPopup(authorizationUrl, 'GoogleSignIn', width, height);

    const handleMessage = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.data.code) {
        const code = event.data.code;
        googleMutation.mutate({ code });
        if (popup) {
          popup.close();
        }
        window.removeEventListener('message', handleMessage);
      }
    };

    window.addEventListener('message', handleMessage, false);
  };

  const signInWithKizeo = () => {
    const redirectUri = import.meta.env.VITE_REDIRECT_URI;
    const responseType = 'code';
    const clientId = import.meta.env.VITE_KIZEO_CLIENT_ID;
    const state = generateRandomHex(48);
    const kizeoOauth2Url = import.meta.env.VITE_KIZEO_OAUTH_URL;
    const authorizationUrl = `${kizeoOauth2Url}/login?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&state=${state}`;

    const width = 500;
    const height = 600;
    const popup = openPopup(authorizationUrl, 'KizeoSignIn', width, height);

    const handleMessage = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.data.code) {
        const code = event.data.code;
        kizeoMutation.mutate({ code });
        if (popup) {
          popup.close();
        }
        window.removeEventListener('message', handleMessage);
      }
    };

    window.addEventListener('message', handleMessage, false);
  };

  const openPopup = (
    url: string,
    title: string,
    width: number,
    height: number,
  ): Window | null => {
    const screenLeft =
      window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const screenTop =
      window.screenTop !== undefined ? window.screenTop : window.screenY;

    const screenWidth = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : window.screen.width;
    const screenHeight = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : window.screen.height;

    const left = screenWidth / 2 - width / 2 + screenLeft;
    const top = screenHeight / 2 - height / 2 + screenTop;

    const popup = window.open(
      url,
      title,
      `scrollbars=yes, width=${width}, height=${height}, top=${top}, left=${left}`,
    );

    if (popup) {
      popup.focus();
    }
    return popup;
  };

  const generateRandomHex = (length: number): string => {
    let result = '';
    const characters = '0123456789abcdef';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  };

  return (
    <Form {...form}>
      <form className="grid space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="grid space-y-2">
              <Label htmlFor="email">{t('Email')}</Label>
              <Input
                {...field}
                required
                id="email"
                type="text"
                placeholder={'email@example.com'}
                className="rounded-sm"
                tabIndex={1}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="grid space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('Password')}</Label>
                {edition !== ApEdition.COMMUNITY && (
                  <Link
                    to="/forget-password"
                    className="text-muted-foreground text-sm hover:text-primary transition-all duration-200"
                  >
                    {t('Forgot your password?')}
                  </Link>
                )}
              </div>
              <Input
                {...field}
                required
                id="password"
                type="password"
                placeholder={'********'}
                className="rounded-sm"
                tabIndex={2}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        {form?.formState?.errors?.root?.serverError && (
          <FormMessage>
            {form.formState.errors.root.serverError.message}
          </FormMessage>
        )}
        <Button
          loading={isPending}
          onClick={(e) => form.handleSubmit(onSubmit)(e)}
          tabIndex={3}
        >
          {t('Sign in')}
        </Button>

        <div className="flex items-center">
          <div className="flex-1 h-[1px] bg-input" />
          <span className="text-sm text-muted-foreground mx-4">
            {t('Or continue with')}
          </span>
          <div className="flex-1 h-[1px] bg-input" />
        </div>

        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={(e) => {
              e.preventDefault();
              signInWithGoogle();
            }}
            tabIndex={4}
          >
            <GoogleIcon className="size-5 mr-2" />
            {t('Google')}
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={(e) => {
              e.preventDefault();
              signInWithKizeo();
            }}
            tabIndex={4}
          >
            <KizeoFormsIcon className="size-5 mr-2" />
            {t('Kizeo')}
          </Button>
        </div>
      </form>
    </Form>
  );
};

SignInForm.displayName = 'SignIn';

export { SignInForm };
